// Badge definitions and unlock criteria. Evaluated server-side on every recompute.
// Each evaluator receives { activities, pointEvents, participant, weeksHeldNumberOne }
// and returns true / false.

export const BADGES = [
  {
    slug: 'acclimatised',
    name: 'Acclimatised',
    description: 'First signal sent. You\'ve acclimatised.',
    howTo: 'Log your first activity — any post, comment, repost or inbound.',
    emoji: '🌱',
    criteria: ({ activities }) => activities.some(a => a.content_gate_passed),
  },
  {
    slug: 'thermalised',
    name: 'Thermalised',
    description: 'Caught a thermal. 5,000 cumulative impressions.',
    howTo: 'Hit 5,000 total impressions across all your posts. Stack as many as you need.',
    emoji: '🌬️',
    criteria: ({ activities }) =>
      activities.reduce((s, a) => s + maxImpressions(a), 0) >= 5000,
  },
  {
    slug: 'stratospheric',
    name: 'Stratospheric',
    description: 'Peak altitude. 20,000 cumulative impressions.',
    howTo: 'Hit 20,000 total impressions across all your posts.',
    emoji: '🛰️',
    criteria: ({ activities }) =>
      activities.reduce((s, a) => s + maxImpressions(a), 0) >= 20000,
  },
  {
    slug: 'sequestered',
    name: 'Sequestered',
    description: 'Captured a qualified lead in the wild.',
    howTo: 'Get a qualified inbound (DM or call) traced to one of your posts. Lennon approves it.',
    emoji: '🌾',
    criteria: ({ activities }) =>
      activities.some(a => a.type === 'inbound' && a.inbound_approved),
  },
  {
    slug: 'positive_feedback',
    name: 'Positive Feedback',
    description: 'Three days of scoring in one week. The loop is live.',
    howTo: 'Log activity on three different days inside the same week (Mon→Sun).',
    emoji: '🌡️',
    criteria: ({ activities }) => {
      const byWeek = new Map()
      for (const a of activities) {
        if (!a.content_gate_passed) continue
        const d = new Date(a.created_at)
        const monday = new Date(d)
        monday.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7))
        const wk = monday.toISOString().slice(0, 10)
        if (!byWeek.has(wk)) byWeek.set(wk, new Set())
        byWeek.get(wk).add(d.toISOString().slice(0, 10))
      }
      return Array.from(byWeek.values()).some(days => days.size >= 3)
    },
  },
  {
    slug: 'net_positive',
    name: 'Net Positive',
    description: 'Held #1 for a week. Better than net zero.',
    howTo: 'Sit at #1 on the leaderboard for a full week.',
    emoji: '🏔️',
    criteria: ({ weeksHeldNumberOne }) => (weeksHeldNumberOne || 0) >= 1,
  },
  {
    slug: 'deep_decarb',
    name: 'Deep Decarb',
    description: 'Twelve qualifying activities. Long-haul work.',
    howTo: 'Log 12 qualifying activities across the quarter — proves you showed up.',
    emoji: '🌲',
    criteria: ({ activities }) =>
      activities.filter(a => a.content_gate_passed).length >= 12,
  },
  {
    slug: 'rewilded',
    name: 'Rewilded',
    description: 'Posted across four different content categories. Range matters.',
    howTo: 'Cover four different content angles (story, technical, behind-the-scenes, industry, data). Lennon awards this manually.',
    emoji: '🦋',
    // Categories were removed from the logging UI, so this can no longer be
    // earned organically. Listed here so it shows in the badge shelf with a
    // sensible hover; admin can award via the DB if someone qualifies.
    criteria: () => false,
  },
]

export function evaluateBadges(input) {
  return BADGES.filter(b => {
    try { return b.criteria(input) }
    catch { return false }
  }).map(b => b.slug)
}

export function getBadgeBySlug(slug) {
  return BADGES.find(b => b.slug === slug)
}

// post_stats is append-only; PostgREST embed order isn't guaranteed, so take
// the highest impression count we've seen for the post (impressions only go up).
function maxImpressions(a) {
  const stats = a.post_stats || []
  return stats.reduce((m, s) => Math.max(m, s?.impressions || 0), 0)
}
